package co.maytes.standupbot.dashboard.features.standups

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.lib.api.ApiClient
import co.maytes.standupbot.dashboard.lib.api.ApiError
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.shared.dto.standuprun.TriggerStandupRunResponseDto
import co.maytes.standupbot.shared.dto.integration.SyncMembersResponseDto
import co.maytes.standupbot.shared.dto.standup.AddMembersRequestDto
import co.maytes.standupbot.shared.dto.standup.CreateStandupRequestDto
import co.maytes.standupbot.shared.dto.standup.ImportChannelMembersRequestDto
import co.maytes.standupbot.shared.dto.github.LinkGithubIdentityRequestDto
import co.maytes.standupbot.shared.dto.standup.LinkLinearIdentityRequestDto
import co.maytes.standupbot.shared.dto.standup.PatchMemberRequestDto
import co.maytes.standupbot.shared.dto.standup.RosterChangeResultDto
import co.maytes.standupbot.shared.dto.standup.StandupDto
import co.maytes.standupbot.shared.dto.standuprun.TriggerStandupRunRequestDto
import co.maytes.standupbot.shared.dto.standup.StandupMemberDto
import co.maytes.standupbot.shared.dto.standup.UpdateStandupRequestDto
import kotlinx.datetime.LocalDate
import io.ktor.client.request.header
import io.ktor.http.HttpMethod
import kotlin.random.Random

sealed interface StandupsListState {
    data object Idle : StandupsListState
    data object Loading : StandupsListState
    data object Ready : StandupsListState
    data class Error(val error: ApiError) : StandupsListState
}

/**
 * Dashboard store for `/api/v1/workspaces/{ws}/standups`. Keeps a cache of the list
 * plus per-standup detail + members, invalidated on any mutation. Mutations return
 * typed Results so UI can gate navigation on success.
 */
class StandupsStore(private val api: ApiClient) {
    var listState: StandupsListState by mutableStateOf(StandupsListState.Idle)
        private set

    var items: List<StandupDto> by mutableStateOf(emptyList())
        private set

    private val detailById = mutableStateMapOf<String, StandupDto>()
    private val membersByTeam = mutableStateMapOf<String, List<StandupMemberDto>>()
    private val membersInFlight = mutableStateMapOf<String, Boolean>()

    fun cachedDetail(standupId: String): StandupDto? = detailById[standupId] ?: items.firstOrNull { it.id == standupId }

    fun cachedMembers(standupId: String): List<StandupMemberDto>? = membersByTeam[standupId]

    fun isFetchingMembers(standupId: String): Boolean = membersInFlight[standupId] == true

    suspend fun list(workspaceId: String) {
        listState = StandupsListState.Loading
        when (val res = api.get<List<StandupDto>>("/api/v1/workspaces/$workspaceId/standups")) {
            is Result.Ok -> {
                items = res.value
                res.value.forEach { detailById[it.id] = it }
                listState = StandupsListState.Ready
            }
            is Result.Err -> listState = StandupsListState.Error(res.error)
        }
    }

    suspend fun detail(workspaceId: String, standupId: String): Result<StandupDto, ApiError> {
        val res = api.get<StandupDto>("/api/v1/workspaces/$workspaceId/standups/$standupId")
        if (res is Result.Ok) detailById[standupId] = res.value
        return res
    }

    suspend fun create(workspaceId: String, body: CreateStandupRequestDto): Result<StandupDto, ApiError> {
        val res = api.post<CreateStandupRequestDto, StandupDto>(
            path = "/api/v1/workspaces/$workspaceId/standups",
            body = body,
        )
        if (res is Result.Ok) {
            items = items + res.value
            detailById[res.value.id] = res.value
        }
        return res
    }

    suspend fun update(
        workspaceId: String,
        standupId: String,
        patch: UpdateStandupRequestDto,
    ): Result<StandupDto, ApiError> {
        val res = api.patch<UpdateStandupRequestDto, StandupDto>(
            path = "/api/v1/workspaces/$workspaceId/standups/$standupId",
            body = patch,
        )
        if (res is Result.Ok) {
            detailById[standupId] = res.value
            items = items.map { if (it.id == standupId) res.value else it }
        }
        return res
    }

    suspend fun delete(workspaceId: String, standupId: String): Result<Unit, ApiError> {
        val res = api.delete<Unit>("/api/v1/workspaces/$workspaceId/standups/$standupId")
        if (res is Result.Ok) {
            items = items.filterNot { it.id == standupId }
            detailById.remove(standupId)
            membersByTeam.remove(standupId)
        }
        return res
    }

    suspend fun trigger(
        workspaceId: String,
        standupId: String,
        targetDate: LocalDate? = null,
    ): Result<TriggerStandupRunResponseDto, ApiError> {
        val key = "trigger-${Random.nextLong().toString(HEX_RADIX)}"
        val path = "/api/v1/workspaces/$workspaceId/standups/$standupId/trigger"
        return api.post<TriggerStandupRunRequestDto, TriggerStandupRunResponseDto>(
            path = path,
            body = TriggerStandupRunRequestDto(targetDate = targetDate),
        ) { header("Idempotency-Key", key) }
    }

    suspend fun members(workspaceId: String, standupId: String): Result<List<StandupMemberDto>, ApiError> {
        membersInFlight[standupId] = true
        val res = api.get<List<StandupMemberDto>>(
            "/api/v1/workspaces/$workspaceId/standups/$standupId/members",
        )
        membersInFlight[standupId] = false
        if (res is Result.Ok) membersByTeam[standupId] = res.value
        return res
    }

    suspend fun patchMember(
        workspaceId: String,
        standupId: String,
        userId: String,
        patch: PatchMemberRequestDto,
    ): Result<StandupMemberDto, ApiError> {
        val res = api.patch<PatchMemberRequestDto, StandupMemberDto>(
            path = "/api/v1/workspaces/$workspaceId/standups/$standupId/members/$userId",
            body = patch,
        )
        if (res is Result.Ok) {
            val current = membersByTeam[standupId].orEmpty()
            membersByTeam[standupId] = current.map { if (it.userId == userId) res.value else it }
        }
        return res
    }

    suspend fun addMembers(
        workspaceId: String,
        standupId: String,
        slackUserIds: List<String>,
    ): Result<List<StandupMemberDto>, ApiError> {
        val res = api.post<AddMembersRequestDto, List<StandupMemberDto>>(
            path = "/api/v1/workspaces/$workspaceId/standups/$standupId/members",
            body = AddMembersRequestDto(slackUserIds),
        )
        if (res is Result.Ok) membersByTeam[standupId] = res.value
        return res
    }

    suspend fun importChannel(
        workspaceId: String,
        standupId: String,
        slackUserIds: List<String>?,
        includeGuests: Boolean,
    ): Result<RosterChangeResultDto, ApiError> {
        val res = api.post<ImportChannelMembersRequestDto, RosterChangeResultDto>(
            path = "/api/v1/workspaces/$workspaceId/standups/$standupId/members/import-channel",
            body = ImportChannelMembersRequestDto(slackUserIds = slackUserIds, includeGuests = includeGuests),
        )
        if (res is Result.Ok) members(workspaceId, standupId)
        return res
    }

    suspend fun resyncIdentityLinks(workspaceId: String): Result<SyncMembersResponseDto, ApiError> =
        api.post<Unit, SyncMembersResponseDto>(
            path = "/api/v1/workspaces/$workspaceId/integrations/sync-members",
            body = Unit,
        )

    suspend fun removeMember(
        workspaceId: String,
        standupId: String,
        userId: String,
    ): Result<Unit, ApiError> {
        val res = api.delete<Unit>("/api/v1/workspaces/$workspaceId/standups/$standupId/members/$userId")
        if (res is Result.Ok) {
            membersByTeam[standupId] = membersByTeam[standupId].orEmpty().filterNot { it.userId == userId }
        }
        return res
    }

    suspend fun linkLinearIdentity(
        workspaceId: String,
        standupId: String,
        userId: String,
        linearUserId: String,
    ): Result<StandupMemberDto, ApiError> {
        val res = api.patch<LinkLinearIdentityRequestDto, StandupMemberDto>(
            path = "/api/v1/workspaces/$workspaceId/standups/$standupId/members/$userId/linear-identity",
            body = LinkLinearIdentityRequestDto(linearUserId),
        )
        if (res is Result.Ok) {
            val updated = res.value
            membersByTeam[standupId] = membersByTeam[standupId].orEmpty().map {
                if (it.userId == updated.userId) updated else it
            }
        }
        return res
    }

    suspend fun linkGithubIdentity(
        workspaceId: String,
        standupId: String,
        userId: String,
        githubLogin: String?,
    ): Result<StandupMemberDto, ApiError> {
        val res = api.patch<LinkGithubIdentityRequestDto, StandupMemberDto>(
            path = "/api/v1/workspaces/$workspaceId/standups/$standupId/members/$userId/github-identity",
            body = LinkGithubIdentityRequestDto(githubLogin),
        )
        if (res is Result.Ok) {
            val updated = res.value
            membersByTeam[standupId] = membersByTeam[standupId].orEmpty().map {
                if (it.userId == updated.userId) updated else it
            }
        }
        return res
    }

    fun invalidateAll() {
        items = emptyList()
        detailById.clear()
        membersByTeam.clear()
        listState = StandupsListState.Idle
    }
}

@kotlinx.serialization.Serializable
private object EmptyBody

private const val HEX_RADIX = 16

@Composable
fun rememberStandupsStore(): StandupsStore {
    val api = rememberApiClient()
    return remember(api) { StandupsStore(api) }
}
